/*
 * Copyright (c) 2025 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/stores/ui-calendar-events/view-specific.stores.ts
 */

import { getPreviousMonday } from '@repo/toolslib/src/utils/date.util';
import { format, isToday, isTomorrow, startOfDay } from 'date-fns';
import { derived, type Readable } from 'svelte/store';

import type { UnionIconValues } from '@repo/common/types/Icon';
import type {
  UICalendarAgendaItem,
  UICalendarDayEvent,
  UICalendarEvent,
  UICalendarEventItem,
  UICalendarMonthEvent,
  UICalendarWeekEvent
} from '@repo/common/types/UICalendar';

// Import core stores
import { getRangeFor } from '../calendar-range.config';
import { calendarViewerStore } from '../viewer.store';
import { uiCalendarEvents, uiCalendarEventsByDate, uiCalendarEventsBySection } from './core.store';
import { groupEventsBySection } from './utils';

/**
 * Overview-specific events with broader date range to include recent past events.
 * Uses the shared uiCalendarEvents core store but filters to a broader range.
 */
const uiCalendarOverviewEventsRaw = derived([uiCalendarEvents], ([$uiCalendarEvents]) => {
  // Use centralized range configuration for overview
  const overviewRange = getRangeFor('OVERVIEW');

  console.log('[Overview] Filtering UI events to overview range:', {
    rangeConfig: 'OVERVIEW',
    start: overviewRange.startISO,
    end: overviewRange.endISO,
    totalEvents: $uiCalendarEvents.length
  });

  // Filter events to overview range
  const filteredEvents = $uiCalendarEvents.filter((event) => {
    return event.start >= overviewRange.start && event.start <= overviewRange.end;
  });

  console.log('[Overview] Filtered events for overview:', {
    filteredCount: filteredEvents.length,
    events: filteredEvents.map((e) => ({
      id: e.id,
      title: e.title,
      start: e.start.toISOString(),
      source: e.source
    }))
  });

  return filteredEvents;
});

/**
 * Overview events sectioned by time for display.
 */
const uiCalendarOverviewEventsBySection = derived([uiCalendarOverviewEventsRaw], ([$events]) => {
  const currentDate = new Date();
  return groupEventsBySection($events, currentDate);
});

/**
 * Overview events store for OverviewCalendarViewer.
 * Pre-computes upcoming and recent event splits with display flags.
 */
export const uiCalendarOverviewEvents = derived(
  [uiCalendarOverviewEventsBySection],
  ([$eventsBySection]) => {
    console.log('[Overview] Final sectioned events:', {
      today: $eventsBySection.today.length,
      tomorrow: $eventsBySection.tomorrow.length,
      upcoming: $eventsBySection.upcoming.length,
      recent: $eventsBySection.recent.length,
      recentEvents: $eventsBySection.recent.map((e) => ({
        id: e.id,
        title: e.title,
        start: e.start.toISOString(),
        source: e.source
      }))
    });

    const now = new Date();
    const upcoming: UICalendarEventItem[] = [
      ...$eventsBySection.today,
      ...$eventsBySection.tomorrow,
      ...$eventsBySection.upcoming
    ]
      .filter((event) => now < event.end) // Only show events that haven't fully ended
      .map((event) => ({
        event,
        showTime: true,
        showStatus: false,
        hasSummary: event.hasSummary,
        isProcessing: event.isProcessing
      }));

    const recent: UICalendarEventItem[] = $eventsBySection.recent
      .sort((a, b) => b.start.getTime() - a.start.getTime()) // Sort newest first
      .slice(0, 10) // Limit to 10 most recent
      .map((event) => ({
        event,
        showTime: true,
        showStatus: true, // Show summary status for past events
        hasSummary: event.hasSummary,
        isProcessing: event.isProcessing
      }));

    return { upcoming, recent };
  }
);

/**
 * Week view events store for CalendarWeekViewer.
 * Pre-computes day columns, positioning, and popover placement.
 */
export const uiCalendarWeekEvents = derived(
  [uiCalendarEventsByDate, calendarViewerStore],
  ([$eventsByDate, $viewerStore]) => {
    if ($viewerStore.timeGrain !== 'week') {
      return []; // Only compute for week view
    }

    const weekEvents: UICalendarWeekEvent[] = [];
    const dayColumns = getDayColumnsForWeek($eventsByDate);

    dayColumns.forEach((events, dayIndex) => {
      events.forEach((event, eventIndex) => {
        weekEvents.push({
          event,
          dayColumn: dayIndex, // 0-6 for Sunday-Saturday
          isOdd: eventIndex % 2 === 1, // For alternating styling
          zoomInFactor: 1.0, // Default zoom level
          popoverPosition: dayIndex < 3 ? 'right' : 'left', // Avoid edge clipping
          overlaps: findOverlappingEvents(event, events) as UICalendarWeekEvent[]
        });
      });
    });

    return weekEvents;
  }
);

/**
 * Month view events store for CalendarMonthViewer.
 * Pre-computes calendar grid positioning and month boundaries.
 */
export const uiCalendarMonthEvents = derived(
  [uiCalendarEventsByDate, calendarViewerStore],
  ([$eventsByDate, $viewerStore]) => {
    if ($viewerStore.timeGrain !== 'month') {
      return []; // Only compute for month view
    }

    const monthEvents: UICalendarMonthEvent[] = [];
    const currentDate = $viewerStore.currentDate ? new Date($viewerStore.currentDate) : new Date();
    const currentMonth = currentDate.getMonth();

    Object.entries($eventsByDate).forEach(([dateStr, events]) => {
      const date = new Date(dateStr);
      const dayOfMonth = date.getDate();
      const weekOfMonth = Math.floor((dayOfMonth - 1) / 7);
      const dayColumn = date.getDay(); // 0-6 for Sunday-Saturday

      events.forEach((event) => {
        monthEvents.push({
          event,
          dayOfMonth,
          weekOfMonth,
          dayColumn,
          isCurrentMonth: date.getMonth() === currentMonth,
          isToday: isToday(date),
          isSelected: false, // Will be set by component based on selection state
          displayText: truncateTitle(event.title, 20) // Truncate for grid display
        });
      });
    });

    return monthEvents;
  }
);

/**
 * Day view events store for CalendarDayViewer.
 * Pre-computes timeline positioning and overlap handling.
 */
export const uiCalendarDayEvents = derived(
  [uiCalendarEventsByDate, calendarViewerStore],
  ([$eventsByDate, $viewerStore]) => {
    if ($viewerStore.timeGrain !== 'day') {
      return []; // Only compute for day view
    }

    // Parse the date string properly to avoid timezone issues
    const currentDate = $viewerStore.currentDate
      ? new Date($viewerStore.currentDate + 'T00:00:00') // Add time to ensure local timezone parsing
      : new Date();
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    const dayEvents = $eventsByDate[dateStr] || [];

    const processedEvents = dayEvents.map((event) => {
      const duration = Math.max(
        (event.end.getTime() - event.start.getTime()) / (1000 * 60),
        30 // Minimum 30 minutes for display
      );

      const position = calculateTimelinePosition(event.start, event.end);
      const overlaps = findOverlappingEvents(event, dayEvents);

      return {
        event,
        startTime: format(event.start, 'h:mm a'),
        endTime: format(event.end, 'h:mm a'),
        duration,
        position: {
          ...position,
          // Adjust for overlaps
          left: overlaps.length > 0 ? 10 : 0,
          width: overlaps.length > 0 ? 90 : 100
        },
        overlaps
      } as UICalendarDayEvent;
    });

    return processedEvents;
  }
);

/**
 * Agenda view events store for CalendarAgenda.
 * Pre-computes time sections and formatting for list display.
 */
export const uiCalendarAgendaItems = derived([uiCalendarEventsBySection], ([$eventsBySection]) => {
  const agendaItems: UICalendarAgendaItem[] = [];

  // Process each section
  const sections = [
    { key: 'today', events: $eventsBySection.today },
    { key: 'tomorrow', events: $eventsBySection.tomorrow },
    { key: 'upcoming', events: $eventsBySection.upcoming }
  ] as const;

  sections.forEach(({ key, events }) => {
    events.forEach((event) => {
      agendaItems.push({
        id: event.id,
        title: event.title,
        time: formatAgendaTime(event),
        category: getEventCategory(event),
        categoryIcon: getEventCategoryIcon(event) as UnionIconValues,
        timeRemaining: event.timeRemaining,
        event,
        section: key
      });
    });
  });

  return agendaItems;
});

// ========================================
// Helper Functions
// ========================================

// Helper functions

/**
 * Get day columns for week view (Sunday-Saturday).
 */
function getDayColumnsForWeek(
  eventsByDate: Record<string, UICalendarEvent[]>
): UICalendarEvent[][] {
  const columns: UICalendarEvent[][] = [[], [], [], [], [], [], []]; // 7 days

  Object.entries(eventsByDate).forEach(([dateStr, events]) => {
    const date = new Date(dateStr);
    const dayColumn = date.getDay(); // 0-6 for Sunday-Saturday
    columns[dayColumn].push(...events);
  });

  return columns;
}

/**
 * Find events that overlap temporally with the given event.
 */
function findOverlappingEvents(
  targetEvent: UICalendarEvent,
  allEvents: UICalendarEvent[]
): UICalendarWeekEvent[] | UICalendarDayEvent[] {
  return allEvents
    .filter((event) => event.id !== targetEvent.id)
    .filter((event) => {
      // Check if events overlap in time
      return targetEvent.start < event.end && targetEvent.end > event.start;
    }) as any; // Type assertion since we're in a generic helper
}

/**
 * Calculate timeline position for day view events.
 */
function calculateTimelinePosition(start: Date, end: Date): { top: number; height: number } {
  const dayStart = startOfDay(start);
  const minutesFromDayStart = (start.getTime() - dayStart.getTime()) / (1000 * 60);
  const durationMinutes = (end.getTime() - start.getTime()) / (1000 * 60);

  // Each hour = 60px, so each minute = 1px
  const top = minutesFromDayStart;
  const height = Math.max(durationMinutes, 30); // Minimum 30px height

  return { top, height };
}

/**
 * Truncate title for grid display.
 */
function truncateTitle(title: string, maxLength: number): string {
  if (title.length <= maxLength) {
    return title;
  }
  return title.substring(0, maxLength - 3) + '...';
}

/**
 * Format time for agenda display.
 */
function formatAgendaTime(event: UICalendarEvent): string {
  const dayOfWeek = event.dayOfWeek;
  const time = format(event.start, 'h:mm a');

  if (event.isToday) {
    return time;
  } else if (isTomorrow(event.start)) {
    return `Tomorrow, ${time}`;
  } else {
    return `${dayOfWeek}, ${time}`;
  }
}

/**
 * Get event category for agenda display.
 */
function getEventCategory(event: UICalendarEvent): string {
  if (event.source === 'recording') {
    return 'Recording';
  }

  // Use calendar name or provider as category
  return event.calendarId || event.providerId || 'Calendar';
}

/**
 * Get event category icon.
 */
function getEventCategoryIcon(event: UICalendarEvent): string {
  if (event.source === 'recording') {
    return '􀊝'; // Microphone icon
  }

  return '􀧸'; // Calendar icon
}

/**
 * Function that creates agenda sections derived store from filtered events.
 * This allows dynamic filtering while maintaining the agenda structure.
 */
export function createUICalendarAgendaSections(eventsStore: Readable<UICalendarEvent[]>) {
  return derived([eventsStore], ([$events]) => {
    console.log('[AgendaSections] Creating agenda sections from events:', {
      eventCount: $events.length,
      sampleEvents: $events
        .slice(0, 3)
        .map((e) => ({ title: e.title, start: e.start.toISOString() }))
    });

    // First convert to agenda items
    const agendaItems: UICalendarAgendaItem[] = [];

    // Group events by section
    $events.forEach((event) => {
      let section: 'today' | 'tomorrow' | 'upcoming';

      if (event.isToday) {
        section = 'today';
      } else if (isTomorrow(event.start)) {
        section = 'tomorrow';
      } else {
        section = 'upcoming';
      }

      agendaItems.push({
        id: event.id,
        title: event.title,
        time: formatAgendaTime(event),
        category: getEventCategory(event),
        categoryIcon: getEventCategoryIcon(event) as UnionIconValues,
        timeRemaining: event.timeRemaining,
        event,
        section
      });
    });

    // Group into sections
    const sections = [
      { title: 'Today', items: [] as UICalendarAgendaItem[] },
      { title: 'Tomorrow', items: [] as UICalendarAgendaItem[] },
      { title: 'Upcoming', items: [] as UICalendarAgendaItem[] }
    ];

    agendaItems.forEach((item) => {
      if (item.section === 'today') {
        sections[0].items.push(item);
      } else if (item.section === 'tomorrow') {
        sections[1].items.push(item);
      } else {
        sections[2].items.push(item);
      }
    });

    // Filter out empty sections
    return sections.filter((section) => section.items.length > 0);
  });
}

// Removed pre-instantiated agenda stores - CalendarNav uses dynamically created stores via NavFilterSet

// ========================================
// CalendarNav-Specific Stores
// ========================================

// Removed filterEventsToNavRange - using centralized range configuration instead

/**
 * CalendarNav events store - filtered to agenda range for extended future visibility.
 * Since CalendarNav is used as the agenda view, it needs the extended agenda range.
 * Range: 1 week back, 10 weeks forward (agenda range).
 */
export const uiCalendarNavEvents = derived([uiCalendarEvents], ([$events]) => {
  // Use agenda range since CalendarNav serves as the agenda view
  const agendaRange = getRangeFor('AGENDA');

  console.log('[CalendarNav] Filtering events to agenda range:', {
    totalEvents: $events.length,
    agendaStart: agendaRange.startISO,
    agendaEnd: agendaRange.endISO,
    rangeDescription: agendaRange
  });

  const filteredEvents = $events.filter((event) => {
    return event.start >= agendaRange.start && event.start <= agendaRange.end;
  });

  // console.log('[CalendarNav] Filtered events result:', {
  //   filteredCount: filteredEvents.length,
  //   futureEvents: filteredEvents.filter((e) => e.isUpcoming).length,
  //   events: filteredEvents.slice(0, 10).map((e) => ({
  //     title: e.title,
  //     start: e.start.toISOString(),
  //     isUpcoming: e.isUpcoming
  //   }))
  // });

  return filteredEvents;
});

/**
 * CalendarNav agenda sections - sectioned for nav display.
 * Creates agenda sections specifically for CalendarNav's filtering needs.
 */
export const uiCalendarNavAgendaSections = createUICalendarAgendaSections(uiCalendarNavEvents);

// ========================================
// Legacy Compatibility Stores
// ========================================

/**
 * Week view store for CalendarWeekViewer.
 * Provides UICalendarEvent[] grouped by day for week view.
 */
export const uiCalendarWeekViewDays = derived(
  [uiCalendarEventsByDate, calendarViewerStore],
  ([$eventsByDate, $viewerStore]) => {
    if ($viewerStore.timeGrain !== 'week') {
      return []; // Only compute for week view
    }

    // Generate 7 days for the week
    const weekStart = $viewerStore.currentDate ? new Date($viewerStore.currentDate) : new Date();
    const days: { date: string; events: UICalendarEvent[]; isToday: boolean }[] = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      const dateStr = format(date, 'yyyy-MM-dd');

      const dayEvents = $eventsByDate[dateStr] || [];

      days.push({
        date: dateStr,
        events: dayEvents,
        isToday: isToday(date)
      });
    }

    return days;
  }
);

/**
 * Month view store for CalendarMonthViewer.
 * Provides UICalendarEvent[] grouped by day for month view.
 */
export const uiCalendarMonthViewDays = derived(
  [uiCalendarEventsByDate, calendarViewerStore],
  ([$eventsByDate, $viewerStore]) => {
    if ($viewerStore.timeGrain !== 'month') {
      return []; // Only compute for month view
    }

    // Generate days for the month view (typically 42 days - 6 weeks)
    const currentDate = $viewerStore.currentDate ? new Date($viewerStore.currentDate) : new Date();
    const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const weekStart = getPreviousMonday(monthStart);
    const days: {
      date: string;
      events: UICalendarEvent[];
      isToday: boolean;
      isCurrentMonth: boolean;
    }[] = [];

    for (let i = 0; i < 42; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      const dateStr = format(date, 'yyyy-MM-dd');

      const dayEvents = $eventsByDate[dateStr] || [];

      days.push({
        date: dateStr,
        events: dayEvents,
        isToday: isToday(date),
        isCurrentMonth: date.getMonth() === currentDate.getMonth()
      });
    }

    return days;
  }
);

/**
 * Day view store for CalendarDayViewer.
 * Provides UICalendarEvent[] for single day.
 */
export const uiCalendarDayViewDays = derived(
  [uiCalendarEventsByDate, calendarViewerStore],
  ([$eventsByDate, $viewerStore]) => {
    if ($viewerStore.timeGrain !== 'day') {
      return []; // Only compute for day view
    }

    const currentDate = $viewerStore.currentDate ? new Date($viewerStore.currentDate) : new Date();
    const dateStr = format(currentDate, 'yyyy-MM-dd');

    const dayEvents = $eventsByDate[dateStr] || [];

    return [
      {
        date: dateStr,
        events: dayEvents,
        isToday: isToday(currentDate)
      }
    ];
  }
);

// Clean up all view-specific stores on HMR
if (typeof import.meta.hot !== 'undefined') {
  import.meta.hot?.dispose(() => {
    console.log('[HMR] Cleaning up view-specific calendar stores');
  });
}
